import { auth, db } from '@/config/firebase';
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import type { Message as SharedMessage, MessagePriority, MessageType } from '@shared/types';

// Re-export enums for convenience
export type { MessagePriority, MessageType };

// Firestore-specific representation (uses Timestamps instead of Date)
export interface Message extends Omit<SharedMessage, 'createdAt' | 'updatedAt' | 'expiresAt' | 'deliveredAt' | 'readAt'> {
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  expiresAt?: Timestamp;
  deliveredAt?: Timestamp;
  readAt?: Timestamp;
}

export interface MessageInput {
  to: string;
  from?: string;
  fromName: string;
  subject: string;
  body: string;
  type: MessageType;
  priority: MessagePriority;
  isImportant?: boolean;
  actionRequired?: boolean;
  expiresAt?: Date;
  avatar?: string;
  conversationId?: string;
  replyTo?: string;
  fromUser?: {
    id: string;
    name: string;
    avatar?: string;
    department?: string;
  };
  toUser?: {
    id: string;
    name: string;
    email?: string;
    department?: string;
  };
}

const MESSAGES_COLLECTION = 'Messages';

const getCurrentUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to perform this action.');
  }
  return user.uid;
};

export const messageService = {
  subscribeToUserMessages(callback: (messages: Message[]) => void): Unsubscribe {
    const user = auth.currentUser;
    if (!user) {
      callback([]);
      return () => {};
    }

    const messagesQuery = query(
      collection(db, MESSAGES_COLLECTION),
      where('to', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messages = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() } as Message))
          .filter((message) => !message.deletedBy?.includes(user.uid));

        callback(messages);
      },
      (error) => {
        console.error('Error subscribing to messages:', error);
        callback([]);
      }
    );
  },

  subscribeToUnreadCount(callback: (count: number) => void): Unsubscribe {
    const user = auth.currentUser;
    if (!user) {
      callback(0);
      return () => {};
    }

    const unreadQuery = query(
      collection(db, MESSAGES_COLLECTION),
      where('to', '==', user.uid),
      where('isRead', '==', false)
    );

    return onSnapshot(
      unreadQuery,
      (snapshot) => {
        const filtered = snapshot.docs.filter((docSnap) => {
          const data = docSnap.data() as Message;
          return !data.deletedBy?.includes(user.uid);
        });
        callback(filtered.length);
      },
      (error) => {
        console.error('Error subscribing to unread messages:', error);
        callback(0);
      }
    );
  },

  subscribeToSentMessages(callback: (messages: Message[]) => void): Unsubscribe {
    const user = auth.currentUser;
    if (!user) {
      callback([]);
      return () => {};
    }

    let unsubscribe: Unsubscribe | null = null;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;

    const startListener = () => {
      unsubscribe?.();
      const sentQuery = query(
        collection(db, MESSAGES_COLLECTION),
        where('from', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(
        sentQuery,
        (snapshot) => {
          const messages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Message));
          callback(messages);
        },
        (error: any) => {
          console.error('Error subscribing to sent messages:', error);
          callback([]);
          unsubscribe?.();
          unsubscribe = null;
          if (error?.code === 'failed-precondition') {
            retryHandle = setTimeout(() => {
              retryHandle = null;
              startListener();
            }, 2000);
          }
        }
      );
    };

    startListener();

    return () => {
      unsubscribe?.();
      if (retryHandle) {
        clearTimeout(retryHandle);
      }
    };
  },

  async markAsRead(messageId: string): Promise<void> {
    await updateDoc(doc(db, MESSAGES_COLLECTION, messageId), {
      isRead: true,
      readAt: serverTimestamp(),
    });
  },

  async markAllAsRead(): Promise<void> {
    const userId = getCurrentUserId();
    const unreadQuery = query(
      collection(db, MESSAGES_COLLECTION),
      where('to', '==', userId),
      where('isRead', '==', false)
    );

    const snapshot = await getDocs(unreadQuery);
    const batch = writeBatch(db);
    snapshot.docs.forEach((docSnap) => {
      batch.update(docSnap.ref, { isRead: true, readAt: serverTimestamp() });
    });

    await batch.commit();
  },

  async toggleImportant(messageId: string, isImportant: boolean): Promise<void> {
    await updateDoc(doc(db, MESSAGES_COLLECTION, messageId), { isImportant });
  },

  async deleteMessage(messageId: string): Promise<void> {
    const userId = getCurrentUserId();
    const messageRef = doc(db, MESSAGES_COLLECTION, messageId);
    await updateDoc(messageRef, {
      deletedBy: arrayUnion(userId),
    });

    const snap = await getDoc(messageRef);
    if (!snap.exists()) return;
    const data = snap.data() as Message;
    const participants = [data.to, data.from].filter(Boolean);
    const deletedBy = data.deletedBy || [];
    const everyoneDeleted = participants.every((participant) =>
      deletedBy.includes(participant as string)
    );

    if (everyoneDeleted) {
      await deleteDoc(messageRef);
    }
  },

  async sendMessage(messageData: MessageInput): Promise<string> {
    const senderId = getCurrentUserId();
    const docRef = await addDoc(collection(db, MESSAGES_COLLECTION), {
      ...messageData,
      from: senderId,
      isRead: false,
      createdAt: serverTimestamp(),
      deliveredAt: serverTimestamp(),
      conversationId:
        messageData.conversationId || `conv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      ...(messageData.expiresAt && { expiresAt: Timestamp.fromDate(messageData.expiresAt) }),
    });

    return docRef.id;
  },

  async replyToMessage(
    originalMessageId: string,
    messageData: Omit<MessageInput, 'replyTo' | 'conversationId'>
  ): Promise<string> {
    const original = await this.getMessageById(originalMessageId);

    return this.sendMessage({
      ...messageData,
      replyTo: originalMessageId,
      conversationId:
        original?.conversationId || `conv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    });
  },

  async getMessageById(messageId: string): Promise<Message | null> {
    const snap = await getDoc(doc(db, MESSAGES_COLLECTION, messageId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as Message;
  },
};
